import { Checkbox } from '../../Checkbox';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { ConfirmationModal } from '../../ConfirmationModal';
import { TextArea } from '../../Inputs/TextArea';
import { useMemo } from 'react';
import { UpdateClassDateModalValues } from '../UpdateClassDateModal';
import { capitalize } from '@mui/material';
import { PaymentStatus } from '../../../services/api.types';

export type ClassEventConfirmationModalValues = Omit<
  UpdateClassDateModalValues,
  'type'
>;

export interface ClassEventConfirmationModalProps {
  mode: 'create' | 'delete' | 'update';
  open: boolean;
  onAccept: () => void;
  onDecline?: () => void;
  onClose: () => void;
  paymentStatusesInRange?: PaymentStatus[];
  needsClassTimeConflictConsent?: boolean;
  needsJustification?: boolean;
  formContext:
    | UseFormReturn<ClassEventConfirmationModalValues>
    | UseFormReturn<UpdateClassDateModalValues>;
}

export const ClassEventConfirmationModal = ({
  mode,
  open,
  onAccept,
  onDecline,
  onClose,
  paymentStatusesInRange,
  needsClassTimeConflictConsent,
  needsJustification,
  formContext,
}: ClassEventConfirmationModalProps) => {
  const eventForm = formContext;

  const modeVerb = useMemo(() => {
    if (mode === 'create') return 'criar';
    if (mode === 'delete') return 'excluir';
    return 'alterar';
  }, [mode]);
  const modeVerbDerivedNoun = useMemo(() => {
    if (mode === 'create') return 'criação';
    if (mode === 'delete') return 'exclusão';
    return 'alteração';
  }, [mode]);
  const modeVerbPastFemale = useMemo(() => {
    if (mode === 'create') return 'criada';
    if (mode === 'delete') return 'excluída';
    return 'alterada';
  }, [mode]);

  const paymentStatusInRange = useMemo(() => {
    if (!paymentStatusesInRange || paymentStatusesInRange.length === 0) return;
    if (paymentStatusesInRange.includes(PaymentStatus.PAID))
      return PaymentStatus.PAID;
    return paymentStatusesInRange[0];
  }, [paymentStatusesInRange]);

  const needsPaymentClassConsent = !!paymentStatusInRange;
  const showConsentDescription =
    needsClassTimeConflictConsent || needsPaymentClassConsent;

  const paymentClassConsentLabel = useMemo(() => {
    if (paymentStatusInRange === PaymentStatus.PAID)
      return `A aula a ser ${modeVerbPastFemale} já foi paga, sua ${modeVerbDerivedNoun} pode acarretar em uma geração de crédito.`;
    return `A aula a ser ${modeVerbPastFemale} tem uma cobrança associada, sua ${modeVerbDerivedNoun} pode acarretar em uma alteração na cobrança existente.`;
  }, [modeVerbDerivedNoun, modeVerbPastFemale, paymentStatusInRange]);

  return (
    <ConfirmationModal
      open={open}
      acceptText={capitalize(modeVerb)}
      declineText="Cancelar"
      onAccept={() => {
        onAccept();
        if (!eventForm.formState.isValid) return;
        onClose();
      }}
      onDecline={() => {
        onDecline?.();
        onClose();
      }}
    >
      <FormProvider
        {...(eventForm as UseFormReturn<ClassEventConfirmationModalValues>)}
      >
        <form className="flex flex-col gap-4">
          {showConsentDescription && (
            <p className="m-0">
              Ao prosseguir, você está ciente das seguintes informações:
            </p>
          )}
          {needsClassTimeConflictConsent && (
            <Checkbox
              name="classTimeConflictConsent"
              label={`A ${modeVerbDerivedNoun} desta aula causará um conflito nas agendas do aluno e/ou do professor.`}
              rules={{ required: true }}
              disabled={!needsClassTimeConflictConsent}
            />
          )}
          {needsPaymentClassConsent && (
            <Checkbox
              name="paymentClassConsent"
              label={paymentClassConsentLabel}
              rules={{ required: true }}
              disabled={!needsPaymentClassConsent}
            />
          )}
          {needsJustification && (
            <TextArea
              label="Justificativa para a mudança nos valores:"
              name="changeValueJustification"
              placeholder="Escreva algum comentário para justificar a alteração de valor"
              rules={{ required: 'Justificativa é obrigatória' }}
              disabled={!needsJustification}
            />
          )}
          <p className="m-0">Tem certeza que deseja {modeVerb} esta aula?</p>
        </form>
      </FormProvider>
    </ConfirmationModal>
  );
};
